/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var forms_1 = require("@angular/forms");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var utils_1 = require("../common/utils");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
/**
 * Represents the [Kendo UI TextArea directive for the Inputs components for Angular]({% slug overview_textarea %}).
 * Provides floating labels to `textarea` elements.
 *
 * @example
 * ```ts-no-run
 * <textarea kendoTextArea></textarea>
 * ```
 */
var TextAreaDirective = /** @class */ (function () {
    function TextAreaDirective(renderer, element, zone, changeDetector, injector, rtl) {
        this.renderer = renderer;
        this.element = element;
        this.zone = zone;
        this.changeDetector = changeDetector;
        this.injector = injector;
        this.elementClasses = true;
        this.autofillClass = true;
        /**
         * Fires each time the textarea value is changed.
         */
        this.valueChange = new core_1.EventEmitter();
        /**
         * Specifies if the `textarea` element will resize its height automatically
         * ([see example]({% slug overview_textarea %}#toc-auto-resizing)).
         *
         * @default false
         */
        this.autoSize = false;
        /**
         * @hidden
         */
        this.onFocus = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.onBlur = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.onValueChange = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.autoFillStart = new core_1.EventEmitter();
        /**
         * @hidden
         */
        this.autoFillEnd = new core_1.EventEmitter();
        this.listeners = [];
        this.ngChange = function (_) { };
        this.ngTouched = function () { };
        this.direction = rtl ? 'rtl' : 'ltr';
    }
    TextAreaDirective_1 = TextAreaDirective;
    Object.defineProperty(TextAreaDirective.prototype, "id", {
        get: function () {
            return this.element.nativeElement.id;
        },
        set: function (id) {
            this.renderer.setAttribute(this.element.nativeElement, 'id', id);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    TextAreaDirective.prototype.writeValue = function (value) {
        this.elementValue = value;
        this.resize();
    };
    /**
     * @hidden
     */
    TextAreaDirective.prototype.registerOnChange = function (fn) {
        this.ngChange = fn;
    };
    /**
     * @hidden
     */
    TextAreaDirective.prototype.registerOnTouched = function (fn) {
        this.ngTouched = fn;
    };
    /**
     * @hidden
     */
    TextAreaDirective.prototype.setDisabledState = function (isDisabled) {
        this.setElementProperty('disabled', isDisabled);
    };
    TextAreaDirective.prototype.ngOnInit = function () {
        var _this = this;
        var element = this.element.nativeElement;
        this.zone.runOutsideAngular(function () {
            _this.listeners = [
                _this.renderer.listen(element, 'focus', _this.handleFocus.bind(_this)),
                _this.renderer.listen(element, 'blur', _this.handleBlur.bind(_this)),
                _this.renderer.listen(element, 'animationstart', function (e) {
                    if (e.animationName === 'autoFillStart') {
                        _this.autoFillStart.emit();
                    }
                    else if (e.animationName === 'autoFillEnd') {
                        _this.autoFillEnd.emit();
                    }
                })
            ];
            if (kendo_angular_common_1.isDocumentAvailable() && _this.autoSize) {
                _this.resizeSubscription = rxjs_1.fromEvent(window, 'resize')
                    .pipe((operators_1.debounceTime(50)))
                    .subscribe(function () { return _this.resize(); });
            }
            _this.inputSubscription = rxjs_1.fromEvent(element, 'input')
                .subscribe(_this.handleInput.bind(_this));
        });
        this.control = this.injector.get(forms_1.NgControl, null);
    };
    TextAreaDirective.prototype.ngOnChanges = function (changes) {
        var _this = this;
        var element = this.element.nativeElement;
        if (changes.value) {
            this.elementValue = this.value;
        }
        if (changes.autoSize) {
            if (this.autoSize) {
                this.initialHeight = element.offsetHeight;
                this.renderer.setStyle(element, 'resize', 'none');
            }
            else {
                this.renderer.setStyle(element, 'overflow-y', 'auto');
                this.renderer.setStyle(element, 'resize', 'both');
                element.style.height = this.initialHeight + "px";
            }
        }
        this.zone.onStable.pipe(operators_1.take(1)).subscribe(function () { return _this.resize(); });
    };
    TextAreaDirective.prototype.ngOnDestroy = function () {
        this.listeners.forEach(function (listener) { return listener(); });
        if (this.inputSubscription) {
            this.inputSubscription.unsubscribe();
        }
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
    };
    Object.defineProperty(TextAreaDirective.prototype, "elementValue", {
        get: function () {
            if (this.element) {
                return this.element.nativeElement.value;
            }
            return '';
        },
        set: function (value) {
            this.setElementProperty('value', (value === undefined || value === null) ? '' : value);
        },
        enumerable: true,
        configurable: true
    });
    TextAreaDirective.prototype.setElementProperty = function (name, value) {
        if (this.element) {
            this.renderer.setProperty(this.element.nativeElement, name, value);
        }
    };
    TextAreaDirective.prototype.resize = function () {
        if (!this.autoSize) {
            return;
        }
        var element = this.element.nativeElement;
        this.renderer.setStyle(element, 'overflow-y', 'hidden');
        element.style.height = this.initialHeight + "px";
        var scrollHeight = element.scrollHeight;
        if (scrollHeight > this.initialHeight) {
            element.style.height = scrollHeight + "px";
        }
    };
    TextAreaDirective.prototype.handleInput = function () {
        var _this = this;
        var value = this.elementValue;
        this.value = value;
        if (this.control || kendo_angular_common_1.hasObservers(this.onValueChange) || kendo_angular_common_1.hasObservers(this.valueChange)) {
            this.zone.run(function () {
                _this.ngChange(value);
                _this.onValueChange.emit(value);
                _this.valueChange.emit(value);
                _this.changeDetector.markForCheck();
            });
        }
        this.resize();
    };
    TextAreaDirective.prototype.handleFocus = function () {
        var _this = this;
        if (kendo_angular_common_1.hasObservers(this.onFocus)) {
            this.zone.run(function () {
                _this.onFocus.emit();
            });
        }
    };
    TextAreaDirective.prototype.handleBlur = function () {
        var _this = this;
        if (kendo_angular_common_1.hasObservers(this.onBlur) || utils_1.requiresZoneOnBlur(this.control)) {
            this.zone.run(function () {
                _this.ngTouched();
                _this.onBlur.emit();
                _this.changeDetector.markForCheck();
            });
        }
    };
    var TextAreaDirective_1;
    tslib_1.__decorate([
        core_1.HostBinding('class.k-textarea'),
        core_1.HostBinding('class.k-input'),
        core_1.HostBinding('class.k-input-md'),
        core_1.HostBinding('class.k-rounded-md'),
        core_1.HostBinding('class.k-input-solid'),
        tslib_1.__metadata("design:type", Boolean)
    ], TextAreaDirective.prototype, "elementClasses", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-autofill'),
        tslib_1.__metadata("design:type", Boolean)
    ], TextAreaDirective.prototype, "autofillClass", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('attr.dir'),
        tslib_1.__metadata("design:type", String)
    ], TextAreaDirective.prototype, "direction", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], TextAreaDirective.prototype, "valueChange", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], TextAreaDirective.prototype, "autoSize", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], TextAreaDirective.prototype, "value", void 0);
    TextAreaDirective = TextAreaDirective_1 = tslib_1.__decorate([
        core_1.Directive({
            providers: [{
                    provide: forms_1.NG_VALUE_ACCESSOR,
                    useExisting: core_1.forwardRef(function () { return TextAreaDirective_1; }),
                    multi: true
                }, {
                    provide: kendo_angular_common_1.KendoInput,
                    useExisting: core_1.forwardRef(function () { return TextAreaDirective_1; })
                }],
            selector: 'textarea[kendoTextArea]'
        }),
        tslib_1.__param(5, core_1.Optional()), tslib_1.__param(5, core_1.Inject(kendo_angular_l10n_1.RTL)),
        tslib_1.__metadata("design:paramtypes", [core_1.Renderer2,
            core_1.ElementRef,
            core_1.NgZone,
            core_1.ChangeDetectorRef,
            core_1.Injector, Boolean])
    ], TextAreaDirective);
    return TextAreaDirective;
}());
exports.TextAreaDirective = TextAreaDirective;
